/**
 * One shared hover tooltip: a small canvas sprite placed just in front of the hit point.
 */
import * as THREE from 'three';
import { PALETTE, font, roundRect } from './charts.ts';

export class Tooltip {
  readonly sprite: THREE.Sprite;
  private readonly canvas = document.createElement('canvas');
  private readonly texture: THREE.CanvasTexture;
  private lines: string[] = [];

  constructor() {
    this.canvas.width = 512;
    this.canvas.height = 160;
    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.colorSpace = THREE.SRGBColorSpace;
    this.sprite = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: this.texture,
        transparent: true,
        depthTest: false,
        toneMapped: false,
      }),
    );
    this.sprite.scale.set(0.5, 0.156, 1);
    this.sprite.center.set(0.5, 0);
    this.sprite.renderOrder = 200;
    this.sprite.visible = false;
  }

  /** Redraws the text if it changed; placement is up to the caller. */
  setLines(lines: string[]): void {
    if (lines.join('\n') !== this.lines.join('\n')) {
      this.lines = lines;
      this.draw();
    }
  }

  show(lines: string[], at: THREE.Vector3, towards: THREE.Vector3): void {
    this.setLines(lines);
    const dir = towards.clone().sub(at).normalize();
    this.sprite.position.copy(at).addScaledVector(dir, 0.08);
    this.sprite.position.y += 0.03;
    this.sprite.visible = true;
  }

  hide(): void {
    this.sprite.visible = false;
  }

  private draw(): void {
    const ctx = this.canvas.getContext('2d')!;
    ctx.clearRect(0, 0, 512, 160);
    const lineH = 30;
    const h = Math.min(160, 20 + this.lines.length * lineH);
    ctx.font = font(22, 500);
    const w = Math.min(512, Math.max(...this.lines.map((l) => ctx.measureText(l).width)) + 36);
    ctx.fillStyle = PALETTE.overlay;
    roundRect(ctx, (512 - w) / 2, 160 - h, w, h, 14);
    ctx.fill();
    ctx.strokeStyle = PALETTE.tealDim;
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.textBaseline = 'top';
    this.lines.slice(0, 4).forEach((l, i) => {
      ctx.font = font(22, i === 0 ? 700 : 500);
      ctx.fillStyle = i === 0 ? PALETTE.text : PALETTE.muted;
      ctx.fillText(l, (512 - w) / 2 + 18, 160 - h + 10 + i * lineH, w - 36);
    });
    this.texture.needsUpdate = true;
  }
}
